import React, { useState, useEffect } from "react";
import api from "../context/api";
import "../styles/AllGames.css";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const AllGames = () => {
  const role = localStorage.getItem("role") || "";
  const isSuperAdmin = role === "superadmin";
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(""); 
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [highWinnersFilter, setHighWinnersFilter] = useState("all");
  const [highWinnersThreshold, setHighWinnersThreshold] = useState(10000);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(500);
  const [hidingGameId, setHidingGameId] = useState("");
  const [superAdminVisibilityFilter, setSuperAdminVisibilityFilter] = useState("all");
  const [userSearchPhone, setUserSearchPhone] = useState("");
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");
  const [userSearchResult, setUserSearchResult] = useState(null);
  const [userEditDraft, setUserEditDraft] = useState({});
  const [complexFieldDraft, setComplexFieldDraft] = useState({});
  const [updatingUser, setUpdatingUser] = useState(false);
  const [inactiveDays, setInactiveDays] = useState("14");
  const [inactiveUsersLoading, setInactiveUsersLoading] = useState(false);
  const [inactiveUsersError, setInactiveUsersError] = useState("");
  const [inactiveUsers, setInactiveUsers] = useState([]);

  const blockedEditFields = ["_id", "password", "__v", "savedNumbers", "createdAt", "updatedAt"];
  const roleOptions = ["user", "admin", "superadmin"];
  const forecasterStatusOptions = ["none", "pending", "approved", "rejected"];

  const dayOptions = [
    "SUNDAY ASEDA",
    "MONDAY SPECIAL",
    "LUCKY TUESDAY",
    "MIDWEEK",
    "FORTUNE THURSDAY",
    "FRIDAY BONANZA",
    "NATIONAL WEEKLY",
    "NLA VAG MONDAY",
    "NLA NOON RUSH MONDAY",
    "HUNTEX MONDAY",
    "NLA VAG TUESDAY",
    "NLA NOON RUSH TUESDAY",
    "HUNTEX AKOMA TUESDAY",
    "NLA VAG WEDNESDAY",
    "NLA NOON RUSH WEDNESDAY",
    "HUNTEX WEDNESDAY",
    "NLA VAG THURSDAY",
    "NLA NOON RUSH THURSDAY",
    "HUNTEX THURSDAY",
    "NLA VAG FRIDAY",
    "NLA NOON RUSH FRIDAY",
    "HUNTEX FRIDAY",
    "NLA VAG SATURDAY",
    "NLA NOON RUSH SATURDAY",
    "HUNTEX SATURDAY",
    "HUNTEX SUNDAY",
    
  ];

  const formatDateForApi = (dateValue) => {
    if (!dateValue) return "";
    const [year, month, day] = dateValue.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  const buildApiParams = ({ forExport = false } = {}) => {
    const params = {};

    if (selectedDate) {
      params.selectedDate = formatDateForApi(selectedDate);
    }

    if (selectedDay) {
      params.selectedDay = selectedDay;
    }

    if (selectedStatus !== "all") {
      params.outcome = selectedStatus;
    }

    if (highWinnersFilter === "high") {
      params.highWinners = 1;
    }

    params.limit = forExport ? 999999 : Number(limit || 0);

    return params;
  };

  const fetchAllGames = async () => {
    setLoading(true);
   
    try {
      const response = await api.get("/allPlayedGames", {
        params: buildApiParams(),
      });
      
      // Handle new response format
      if (response.data.success) {
        const fetchedGames = response.data.games || [];
        const thresholdFromServer = Number(response.data.highWinnersMinAmount);
        if (Number.isFinite(thresholdFromServer) && thresholdFromServer > 0) {
          setHighWinnersThreshold(thresholdFromServer);
        }
        setGames(fetchedGames);
        setTotalCount(response.data.totalCount || fetchedGames.length);
      } else {
        // Fallback for old format
        const fetchedGames = Array.isArray(response.data) ? response.data : [];
        setGames(fetchedGames);
        setTotalCount(fetchedGames.length);
      }
    } catch (error) {
     
      setGames([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const isComplexFieldValue = (value) => {
    return value !== null && typeof value === "object";
  };

  const buildEditableDrafts = (user) => {
    const nextDraft = {};
    const nextComplex = {};

    Object.entries(user || {}).forEach(([key, value]) => {
      if (blockedEditFields.includes(key)) return;
      nextDraft[key] = value;
      if (isComplexFieldValue(value)) {
        nextComplex[key] = JSON.stringify(value, null, 2);
      }
    });

    return { nextDraft, nextComplex };
  };

  const handlePrimitiveFieldChange = (field, rawValue, valueType) => {
    let nextValue = rawValue;

    if (valueType === "number") {
      nextValue = rawValue === "" ? "" : Number(rawValue);
    }

    if (valueType === "boolean") {
      nextValue = rawValue === "true";
    }

    setUserEditDraft((prev) => ({
      ...prev,
      [field]: nextValue,
    }));
  };

  const handleComplexFieldChange = (field, rawValue) => {
    setComplexFieldDraft((prev) => ({
      ...prev,
      [field]: rawValue,
    }));
  };

  const handleSuperAdminUserSearch = async () => {
    const phone = String(userSearchPhone || "").trim();
    if (!phone) {
      setUserSearchError("Enter a phone number to search.");
      return;
    }

    setUserSearchLoading(true);
    setUserSearchError("");
    setUserSearchResult(null);
    setUserEditDraft({});
    setComplexFieldDraft({});

    try {
      const response = await api.get("/superadmin/users/by-phone", {
        params: { phoneNumber: phone },
      });

      const foundUser = response?.data?.user;
      if (!foundUser) {
        setUserSearchError("User not found.");
        return;
      }

      setUserSearchResult(foundUser);
      const { nextDraft, nextComplex } = buildEditableDrafts(foundUser);
      setUserEditDraft(nextDraft);
      setComplexFieldDraft(nextComplex);
    } catch (error) {
      setUserSearchError(error?.response?.data?.error || "Failed to search user.");
    } finally {
      setUserSearchLoading(false);
    }
  };

  const [deletingUser, setDeletingUser] = useState(false);

  const handleSuperAdminUserDelete = async () => {
    if (!userSearchResult?._id) {
      setUserSearchError("Search for a user first.");
      return;
    }

    const confirmed = window.confirm(
      `Permanently delete "${userSearchResult.fullName || userSearchResult.phoneNumber || "this user"}"?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingUser(true);
    setUserSearchError("");

    try {
      await api.delete(`/superadmin/users/${userSearchResult._id}`);
      alert("User deleted successfully.");
      setUserSearchResult(null);
      setUserEditDraft({});
      setComplexFieldDraft({});
      setUserSearchPhone("");
    } catch (error) {
      setUserSearchError(error?.response?.data?.error || "Failed to delete user.");
    } finally {
      setDeletingUser(false);
    }
  };

  const handleSuperAdminUserUpdate = async () => {
    if (!userSearchResult?._id) {
      setUserSearchError("Search for a user first.");
      return;
    }

    const payload = { ...userEditDraft };

    const complexFields = Object.keys(complexFieldDraft || {});
    for (const field of complexFields) {
      const raw = complexFieldDraft[field];
      try {
        payload[field] = JSON.parse(raw);
      } catch (error) {
        setUserSearchError(`Invalid JSON in field: ${field}`);
        return;
      }
    }

    blockedEditFields.forEach((key) => {
      delete payload[key];
    });

    setUpdatingUser(true);
    setUserSearchError("");

    try {
      const response = await api.patch(`/superadmin/users/${userSearchResult._id}`, payload);
      const updatedUser = response?.data?.user;
      if (updatedUser) {
        setUserSearchResult(updatedUser);
        const { nextDraft, nextComplex } = buildEditableDrafts(updatedUser);
        setUserEditDraft(nextDraft);
        setComplexFieldDraft(nextComplex);
      }
      alert("User updated successfully.");
    } catch (error) {
      setUserSearchError(error?.response?.data?.error || "Failed to update user.");
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleToggleHide = async (game) => {
    const gameId = game?._id || game?.id;
    if (!gameId || !game?.phoneNumber || !isSuperAdmin) return;

    const nextHideValue = !Boolean(game.isHidden);
    setHidingGameId(String(gameId));

    try {
      await api.post("/hideGame", {
        phoneNumber: game.phoneNumber,
        gameId: String(gameId),
        hide: nextHideValue,
      });

      setGames((prev) =>
        prev.map((item) => {
          const sameGame = String(item._id || item.id) === String(gameId);
          if (!sameGame) return item;
          return {
            ...item,
            isHidden: nextHideValue,
            hiddenAt: nextHideValue ? new Date().toISOString() : null,
          };
        })
      );
    } catch (error) {
      const serverMessage = error?.response?.data?.error;
      alert(serverMessage || "Unable to update hide status for this game.");
    } finally {
      setHidingGameId("");
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "No activity";
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) return "No activity";
    return parsedDate.toLocaleString();
  };

  const formatDaysInactive = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric >= 0 ? numeric : "No history";
  };

  const handleFetchInactiveUsers = async () => {
    setInactiveUsersLoading(true);
    setInactiveUsersError("");

    try {
      const response = await api.get("/superadmin/users/inactive", {
        params: {
          days: Number(inactiveDays),
          limit: 50000,
        },
        timeout: 30000,
      });

      setInactiveUsers(Array.isArray(response?.data?.users) ? response.data.users : []);
    } catch (error) {
      setInactiveUsers([]);
      const responseData = error?.response?.data;
      const timeoutError = error?.code === "ECONNABORTED";
      const networkError = !error?.response && !timeoutError;
      let nextError =
        responseData?.error ||
        responseData?.message ||
        responseData?.details ||
        "";

      if (responseData?.details) {
        nextError = `${nextError} (${responseData.details})`;
      }

      if (!nextError && timeoutError) {
        nextError = "Request timed out while fetching inactive users. Please try again.";
      }

      if (!nextError && networkError) {
        nextError = "Network error while fetching inactive users. Check connection and backend status.";
      }

      if (!nextError && typeof responseData === "string") {
        if (responseData.includes("Cannot GET /superadmin/users/inactive")) {
          nextError = "Inactive users endpoint not found on backend. Restart/redeploy backend with latest code.";
        } else {
          nextError = responseData;
        }
      }

      setInactiveUsersError(nextError || "Failed to fetch inactive users.");
    } finally {
      setInactiveUsersLoading(false);
    }
  };

  const exportInactiveUsersToCSV = () => {
    if (!Array.isArray(inactiveUsers) || inactiveUsers.length === 0) {
      alert("No inactive users to export.");
      return;
    }

    const headers = [
      "Name",
      "Phone",
      "Role",
      "Last Activity",
      "Days Inactive",
      "Tickets",
      "Activity Status",
      "Balance",
    ];

    const escapeCSV = (value) => {
      const text = String(value ?? "");
      return `"${text.replace(/"/g, '""')}"`;
    };

    const rows = inactiveUsers.map((user) => [
      user.fullName || "N/A",
      user.phoneNumber || "N/A",
      user.role || "user",
      user.lastActivityAt || "No activity",
      formatDaysInactive(user.daysInactive),
      Number(user.totalTickets || 0),
      user.activityStatus || "inactive",
      Number(user.userBalance || 0).toFixed(2),
    ]);

    const csvContent = [
      headers.map(escapeCSV).join(","),
      ...rows.map((row) => row.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const filename = `inactive_users_${inactiveDays}d_${timestamp}.csv`;

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredGames = isSuperAdmin
    ? games.filter((game) => {
        if (superAdminVisibilityFilter === "hidden") return game.isHidden === true;
        if (superAdminVisibilityFilter === "visible") return game.isHidden !== true;
        return true;
      })
    : games;

  useEffect(() => {
    fetchAllGames();
  }, []);

  const totalStakes = games.reduce((sum, game) => sum + (Number(game.price) || 0), 0);
  const filteredTotalStakes = filteredGames.reduce((sum, game) => sum + (Number(game.price) || 0), 0);
  const totalPotentialWin = games.reduce((sum, game) => sum + (Number(game.maximumWin) || 0), 0);
  const totalWinAmount = games
    .filter(game => game.outcome === "Won")
    .reduce((sum, game) => sum + (Number(game.winPrice) || 0), 0);
  const wonGamesCount = games.filter(game => game.outcome === "Won").length;
  const lostGamesCount = games.filter(game => game.outcome === "Lost").length;
  const pendingGamesCount = games.filter(game => game.outcome === "Pending" || !game.outcome).length;
  
  const totalLostAmount = games
    .filter(game => game.outcome === "Lost")
    .reduce((sum, game) => sum + (Number(game.price) || 0), 0);
  
  // Analytics data for charts
  const winLossData = [
    { name: "Won", value: wonGamesCount, color: "#28a745" },
    { name: "Lost", value: lostGamesCount, color: "#dc3545" },
    { name: "Pending", value: pendingGamesCount, color: "#ffc107" }
  ];
  
  const revenueData = [
    { 
      name: "Financial Overview", 
      "Total Stakes": totalStakes, 
      "Total Payouts": totalWinAmount,
      "Net Revenue": totalStakes - totalWinAmount
    }
  ];

  const getLimitLabel = (limit) => {
    // Handle if limit is a string - try to parse it
    let parsedLimit = limit;
    if (typeof limit === 'string') {
      try {
        parsedLimit = JSON.parse(limit);
      } catch (e) {
        // If can't parse, return the string as-is or 'N/A'
        return limit || 'N/A';
      }
    }
    
    // Convert array elements to numbers if they are strings
    if (Array.isArray(parsedLimit)) {
      parsedLimit = parsedLimit.map(val => typeof val === 'string' ? Number(val) : val);
    }
    
    switch (JSON.stringify(parsedLimit)) {
      case JSON.stringify([1, 2]):
        return 'DIRECT 2';
      case JSON.stringify([1, 3]):
        return 'DIRECT 3';
      case JSON.stringify([3, 24]):
        return 'PERM 2';
      case JSON.stringify([4, 17]):
        return 'PERM 3';
      case JSON.stringify([5, 13]):
        return 'PERM 4';
      case JSON.stringify([6, 12]):
        return 'PERM 5';
      case JSON.stringify([1, 1]):
        return 'Banker Against All';
      case JSON.stringify([1, 24]):
        return 'Banker Against';
      default:
        return parsedLimit ? JSON.stringify(parsedLimit) : 'N/A';
    }
  };

  const isGameClosed = (game) => {
    if (game?.expired === true) {
      return true;
    }

    try {
      if (!game?.NextGameDate || !game?.closingTime) {
        return false;
      }

      let closingDateTime = new Date(`${game.NextGameDate} ${game.closingTime}`);

      if (Number.isNaN(closingDateTime.getTime())) {
        const baseDate = new Date(game.NextGameDate);
        if (Number.isNaN(baseDate.getTime())) {
          return false;
        }

        const timeParts = String(game.closingTime).split(":");
        if (timeParts.length < 2) {
          return false;
        }

        const hours = Number.parseInt(timeParts[0], 10);
        const minutes = Number.parseInt(timeParts[1], 10);

        if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
          return false;
        }

        closingDateTime = new Date(baseDate.getTime());
        closingDateTime.setHours(hours, minutes, 0, 0);
      }

      return new Date() >= closingDateTime;
    } catch (error) {
      return false;
    }
  };

  const isGameResultPublished = (game) => {
    const outcome = String(game?.outcome || "").trim().toUpperCase();
    return outcome === "WON" || outcome === "LOST";
  };

  const canExportCSV =
    totalCount > 0 &&
    !loading &&
    filteredGames.length > 0 &&
    (isSuperAdmin || filteredGames.every((game) => isGameResultPublished(game)));

  const exportToCSV = async () => {
    if (totalCount === 0) {
      alert("No games to export");
      return;
    }

    if (!isSuperAdmin && filteredGames.some((game) => !isGameResultPublished(game))) {
      alert("Download CSV.");
      return;
    }

    try {
      // Fetch ALL games matching filters for export
      const response = await api.get("/allPlayedGames", {
        params: buildApiParams({ forExport: true }),
      });
      
      let exportGames = [];
      if (response.data.success) {
        exportGames = response.data.games || [];
      } else {
        exportGames = Array.isArray(response.data) ? response.data : [];
      }

      if (exportGames.length === 0) {
        alert("No games to export");
        return;
      }

      if (!isSuperAdmin && exportGames.some((game) => !isGameResultPublished(game))) {
        alert("Download CSV.");
        return;
      }

      // Define CSV headers
      const headers = [
        "Player Name",
        "Phone Number",
        "Game Date",
        "Game Day",
        "Closing Time",
        "Game Type",
        "Selected Numbers",
        "Against Numbers",
        "Stake (GHC)",
        "Minimum Win (GHC)",
        "Maximum Win (GHC)",
        "Win Amount (GHC)",
        "Status",
        "Common Numbers",
        "Purchased Date/Time",
        "Paid",
        "Expired",
        "Processed"
      ];

      // Convert games to CSV rows
      const rows = exportGames.map(game => [
        game.fullName || "Unknown",
        game.phoneNumber || "N/A",
        game.NextGameDate || "",
        game.selectedDay || "",
        game.closingTime || "",
        game.limitLabel || getLimitLabel(game.limit) || "N/A",
        (game.selectedNumbers || []).join("; "),
        (game.againstNumbers || []).join("; "),
        Number(game.price || 0).toFixed(2),
        Number(game.minimumWin || 0).toFixed(2),
        Number(game.maximumWin || 0).toFixed(2),
        Number(game.winPrice || 0).toFixed(2),
        game.outcome || "Pending",
        (game.commonNumbers || []).join("; "),
        game.DateTime || "",
        game.paid ? "Yes" : "No",
        game.expired ? "Yes" : "No",
        game.processed ? "Yes" : "No"
      ]);

      // Combine headers and rows
      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
      ].join("\n");

      // Create blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url2 = URL.createObjectURL(blob);
      
      // Generate filename with date and filters
      const dateStr = selectedDate ? selectedDate.replace(/-/g, "") : "all";
      const dayStr = selectedDay ? selectedDay.replace(/\s+/g, "_") : "all_days";
      const timestamp = new Date().toISOString().split("T")[0].replace(/-/g, "");
      const filename = `games_${dateStr}_${dayStr}_${timestamp}.csv`;
      
      link.setAttribute("href", url2);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      alert("Error exporting games: " + error.message);
    }
  };

  return (
    <div className="all-games-container">
      <header className="games-header">
        <h1 className="games-title">{isSuperAdmin ? "Super Admin All Games" : "All Games"}</h1>
        <p className="games-subtitle">
          {isSuperAdmin
            ? "Manage full game stream and hide games from regular admins"
            : "View all games played by users"}
        </p>
      </header>

      {isSuperAdmin && (
        <>
          <div className="superadmin-user-card">
            <h2 className="superadmin-user-title">Super Admin User Search & Edit</h2>
            <p className="superadmin-user-subtitle">
              Search by phone number, review full user data, and update any editable field.
            </p>

            <div className="superadmin-user-actions">
              <input
                type="text"
                className="filter-input"
                value={userSearchPhone}
                onChange={(e) => setUserSearchPhone(e.target.value)}
                placeholder="Enter phone number"
              />
              <button
                type="button"
                className="search-btn"
                onClick={handleSuperAdminUserSearch}
                disabled={userSearchLoading}
              >
                {userSearchLoading ? "Searching..." : "Search User"}
              </button>
              <button
                type="button"
                className="search-btn"
                onClick={handleSuperAdminUserUpdate}
                disabled={updatingUser || !userSearchResult}
                style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #0369a1 100%)" }}
              >
                {updatingUser ? "Updating..." : "Save User Changes"}
              </button>
              <button
                type="button"
                className="search-btn"
                onClick={handleSuperAdminUserDelete}
                disabled={deletingUser || !userSearchResult}
                style={{ background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)" }}
              >
                {deletingUser ? "Deleting..." : "Delete User"}
              </button>
            </div>

            {userSearchError && <p className="superadmin-user-error">{userSearchError}</p>}

            {userSearchResult && (
              <div className="superadmin-user-result">
                <div className="superadmin-user-meta">
                  <span><strong>User ID:</strong> {userSearchResult._id}</span>
                  <span><strong>Name:</strong> {userSearchResult.fullName || "N/A"}</span>
                  <span><strong>Phone:</strong> {userSearchResult.phoneNumber || "N/A"}</span>
                  <span><strong>Role:</strong> {userSearchResult.role || "user"}</span>
                </div>

                <div className="superadmin-user-preview">
                  <label className="filter-label">All User Information (Read-only)</label>
                  <pre className="superadmin-user-preview-json">
                    {JSON.stringify(userSearchResult, null, 2)}
                  </pre>
                </div>

                <div className="superadmin-user-editor">
                  <label className="filter-label">Editable User Fields</label>
                  <div className="superadmin-field-grid">
                    {Object.entries(userEditDraft).map(([field, value]) => {
                      const valueType = typeof value;
                      const isComplex = isComplexFieldValue(value);

                      return (
                        <div className="superadmin-field-item" key={field}>
                          <label className="superadmin-field-label">{field}</label>

                          {isComplex ? (
                            <textarea
                              className="superadmin-user-json"
                              value={complexFieldDraft[field] || ""}
                              onChange={(e) => handleComplexFieldChange(field, e.target.value)}
                              rows={6}
                              spellCheck={false}
                            />
                          ) : field === "role" ? (
                            <select
                              className="filter-input"
                              value={String(value ?? "")}
                              onChange={(e) => handlePrimitiveFieldChange(field, e.target.value, "string")}
                            >
                              {roleOptions.map((option) => (
                                <option value={option} key={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : field === "forecasterApprovalStatus" ? (
                            <select
                              className="filter-input"
                              value={String(value ?? "")}
                              onChange={(e) => handlePrimitiveFieldChange(field, e.target.value, "string")}
                            >
                              {forecasterStatusOptions.map((option) => (
                                <option value={option} key={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : valueType === "boolean" ? (
                            <select
                              className="filter-input"
                              value={String(Boolean(value))}
                              onChange={(e) => handlePrimitiveFieldChange(field, e.target.value, "boolean")}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          ) : valueType === "number" ? (
                            <input
                              type="number"
                              className="filter-input"
                              value={value ?? ""}
                              onChange={(e) => handlePrimitiveFieldChange(field, e.target.value, "number")}
                            />
                          ) : (
                            <input
                              type="text"
                              className="filter-input"
                              value={value ?? ""}
                              onChange={(e) => handlePrimitiveFieldChange(field, e.target.value, "string")}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="superadmin-user-card">
            <h2 className="superadmin-user-title">Inactive Users</h2>
            <p className="superadmin-user-subtitle">
              Find accounts with no activity for 2 weeks, 3 weeks, or 1 month.
            </p>

            <div className="superadmin-user-actions inactive-user-actions">
              <select
                className="filter-input"
                value={inactiveDays}
                onChange={(e) => setInactiveDays(e.target.value)}
              >
                <option value="14">2 weeks (14 days)</option>
                <option value="21">3 weeks (21 days)</option>
                <option value="30">1 month (30 days)</option>
              </select>
              <button
                type="button"
                className="search-btn"
                onClick={handleFetchInactiveUsers}
                disabled={inactiveUsersLoading}
              >
                {inactiveUsersLoading ? "Loading..." : "Get Inactive Users"}
              </button>
              <button
                type="button"
                className="search-btn"
                onClick={exportInactiveUsersToCSV}
                disabled={inactiveUsersLoading || inactiveUsers.length === 0}
                style={{ backgroundColor: "#10b981" }}
              >
                Export Inactive CSV ({inactiveUsers.length})
              </button>
            </div>

            {inactiveUsersError && <p className="superadmin-user-error">{inactiveUsersError}</p>}

            <p className="inactive-user-count">
              {inactiveUsers.length} user(s) inactive for at least {inactiveDays} days.
            </p>

            {inactiveUsers.length > 0 && (
              <div className="inactive-users-table-wrap">
                <table className="inactive-users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Role</th>
                      <th>Last Activity</th>
                      <th>Days Inactive</th>
                      <th>Tickets</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveUsers.map((user) => (
                      <tr key={String(user._id)}>
                        <td>{user.fullName || "N/A"}</td>
                        <td>{user.phoneNumber || "N/A"}</td>
                        <td>{user.role || "user"}</td>
                        <td>{formatDateTime(user.lastActivityAt)}</td>
                        <td>{formatDaysInactive(user.daysInactive)}</td>
                        <td>{Number(user.totalTickets || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      <div className="filters-section">
        <div className="filters-card">
          <div className="filter-group">
            <label className="filter-label">Game Date</label>
            <input
              type="date"
              className="filter-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              placeholder="Filter by game date"
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Game Day</label>
            <select
              className="filter-input"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              <option value="">All Days</option>
              {dayOptions.map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Status</label>
            <select
              className="filter-input"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Won">Won</option>
              <option value="Lost">Lost</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">High Winners</label>
            <select
              className="filter-input"
              value={highWinnersFilter}
              onChange={(e) => setHighWinnersFilter(e.target.value)}
            >
              <option value="all">All Stakes</option>
              <option value="high">Above {highWinnersThreshold} (Min/Max Win)</option>
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Results Limit</label>
            <select
              className="filter-input"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value="100">100 games</option>
              <option value="200">200 games</option>
              <option value="500">500 games</option>
              <option value="1000">1000 games</option>
              <option value="10000">10000 games</option>
            </select>
          </div>

          {isSuperAdmin && (
            <div className="filter-group">
              <label className="filter-label">Visibility</label>
              <select
                className="filter-input"
                value={superAdminVisibilityFilter}
                onChange={(e) => setSuperAdminVisibilityFilter(e.target.value)}
              >
                <option value="all">All Games</option>
                <option value="visible">Visible To Admin</option>
                <option value="hidden">Hidden From Admin</option>
              </select>
            </div>
          )}

          <button className="search-btn" onClick={fetchAllGames} disabled={loading}>
            {loading ? "Searching..." : "Search Games"}
          </button>

          <button 
            className="search-btn" 
            onClick={exportToCSV} 
            
            style={{ backgroundColor: "#10b981" }}
            title={
              !totalCount
                ? "No games to export"
                : !isSuperAdmin && filteredGames.some((game) => !isGameResultPublished(game))
                  ? "Download CSV"
                  : "Download CSV"
            }
          >
            📥 Export to CSV ({totalCount})
          </button>
        </div>

        <div className="stats-section">
          <div className="stat-card">
            <span className="stat-icon">🎮</span>
            <div className="stat-info">
              <span className="stat-value">{filteredGames.length}</span>
              <span className="stat-label">Showing Games</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">📊</span>
            <div className="stat-info">
              <span className="stat-value">{totalCount}</span>
              <span className="stat-label">Total Matches</span>
            </div>
          </div>
          <div className="stat-card">
            <span className="stat-icon">💰</span>
            <div className="stat-info">
              <span className="stat-value">
                GHC {(isSuperAdmin && superAdminVisibilityFilter === "hidden" ? filteredTotalStakes : totalStakes).toLocaleString()}
              </span>
              <span className="stat-label">
                {isSuperAdmin && superAdminVisibilityFilter === "hidden" ? "Hidden Games Stakes" : "Total Stakes"}
              </span>
            </div>
          </div>
          
          <div className="stat-card">
            <span className="stat-icon">🏆</span>
            <div className="stat-info">
              <span className="stat-value">GHC {totalPotentialWin.toLocaleString()}</span>
              <span className="stat-label">Potential Win</span>
            </div>
          </div>
          
          {(selectedStatus === "Won" || wonGamesCount > 0) && (
            <div className="stat-card won-highlight">
              <span className="stat-icon">💵</span>
              <div className="stat-info">
                <span className="stat-value">GHC {totalWinAmount.toLocaleString()}</span>
                <span className="stat-label">Total Win Amount ({wonGamesCount})</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Business Analytics Charts */}
      {games.length > 0 && (
        <div className="analytics-section">
          <h2 className="analytics-title">Business Analytics & Insights</h2>
          
          <div className="charts-container">
            {/* Win/Loss Distribution Pie Chart */}
            <div className="chart-card">
              <h3 className="chart-title">Win/Loss Distribution</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={winLossData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {winLossData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `${value} games`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
              <div className="chart-stats">
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#28a745"}}></span>
                  <span>Won: {wonGamesCount} ({((wonGamesCount / games.length) * 100).toFixed(1)}%)</span>
                </div>
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#dc3545"}}></span>
                  <span>Lost: {lostGamesCount} ({((lostGamesCount / games.length) * 100).toFixed(1)}%)</span>
                </div>
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#ffc107"}}></span>
                  <span>Pending: {pendingGamesCount} ({((pendingGamesCount / games.length) * 100).toFixed(1)}%)</span>
                </div>
              </div>
            </div>

            {/* Revenue Analysis Bar Chart */}
            <div className="chart-card">
              <h3 className="chart-title">Financial Performance</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip formatter={(value) => `GHC ${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="Total Stakes" fill="#3b82f6" />
                  <Bar dataKey="Total Payouts" fill="#ef4444" />
                  <Bar dataKey="Net Revenue" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
              <div className="chart-stats">
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#3b82f6"}}></span>
                  <span>Total Stakes: GHC {totalStakes.toLocaleString()}</span>
                </div>
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#ef4444"}}></span>
                  <span>Total Payouts: GHC {totalWinAmount.toLocaleString()}</span>
                </div>
                <div className="chart-stat-item">
                  <span className="stat-dot" style={{backgroundColor: "#10b981"}}></span>
                  <span>Net Revenue: GHC {(totalStakes - totalWinAmount).toLocaleString()}</span>
                </div>
                <div className="chart-stat-item profit-margin">
                  <span>💹 Profit Margin: {totalStakes > 0 ? (((totalStakes - totalWinAmount) / totalStakes) * 100).toFixed(2) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Key Business Insights */}
          <div className="insights-card">
            <h3 className="insights-title">📈 Key Business Insights</h3>
            <div className="insights-grid">
              <div className="insight-item">
                <span className="insight-label">Win Rate:</span>
                <span className="insight-value">{games.length > 0 ? ((wonGamesCount / games.length) * 100).toFixed(2) : 0}%</span>
              </div>
              <div className="insight-item">
                <span className="insight-label">Average Stake:</span>
                <span className="insight-value">GHC {games.length > 0 ? (totalStakes / games.length).toFixed(2) : 0}</span>
              </div>
              <div className="insight-item">
                <span className="insight-label">Average Payout:</span>
                <span className="insight-value">GHC {wonGamesCount > 0 ? (totalWinAmount / wonGamesCount).toFixed(2) : 0}</span>
              </div>
              <div className="insight-item">
                <span className="insight-label">Payout Ratio:</span>
                <span className="insight-value">{totalStakes > 0 ? ((totalWinAmount / totalStakes) * 100).toFixed(2) : 0}%</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="games-list-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading games...</p>
          </div>
        ) : filteredGames.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🎲</span>
            <h3>No Games Found</h3>
            <p>Try adjusting your filters</p>
          </div>
        ) : (
          <div className="games-grid">
            {filteredGames.map((game, index) => (
              <div className="game-card" key={game._id || index}>
                <div className="game-header">
                  <span className={`status-badge ${game.outcome?.toLowerCase() || "pending"}`}>
                    {game.outcome || "Pending"}
                  </span>
                  <span className="game-date">{game.NextGameDate}</span>
                </div>
                <div className="game-body">
                  <div className="player-info">
                    <span className="player-name">{game.fullName || "Unknown"}</span>
                    <span className="player-phone">{game.phoneNumber || "N/A"}</span>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Purchased:</span>
                    <span className="detail-value">{game.DateTime || "N/A"}</span>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Day:</span>
                    <span className="detail-value">{game.selectedDay}</span>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Numbers:</span>
                    <div className="numbers-display">
                      {game.selectedNumbers?.map((num, idx) => (
                        <span key={idx} className="number-badge">
                          {num}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Stake:</span>
                    <span className="detail-value">GHC {Number(game.price || 0).toLocaleString()}</span>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Game Type:</span>
                    <span className="detail-value">{game.limitLabel || getLimitLabel(game.limit)}</span>
                  </div>
                  <div className="game-detail">
                    <span className="detail-label">Potential Win:</span>
                    <span className="detail-value win">
                      GHC {Number(game.minimumWin || 0).toLocaleString()} - {Number(game.maximumWin || 0).toLocaleString()}
                    </span>
                  </div>
                  {game.outcome === "Won" && (
                    <div className="game-detail win-price">
                      <span className="detail-label">Win Amount:</span>
                      <span className="detail-value">GHC {Number(game.winPrice || 0).toLocaleString()}</span>
                    </div>
                  )}
                  {isSuperAdmin && (
                    <div className="game-detail" style={{ marginTop: "10px", gap: "8px", alignItems: "center" }}>
                      <span className="detail-label">Admin Visibility:</span>
                      <span className="detail-value">{game.isHidden ? "Hidden" : "Visible"}</span>
                      <button
                        type="button"
                        className="search-btn"
                        style={{ marginLeft: "auto", padding: "6px 10px", fontSize: "12px" }}
                        disabled={hidingGameId === String(game._id || game.id)}
                        onClick={() => handleToggleHide(game)}
                      >
                        {hidingGameId === String(game._id || game.id)
                          ? "Updating..."
                          : game.isHidden
                            ? "Unhide"
                            : "Hide"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllGames;
